import { DestroyRef, inject, signal, WritableSignal } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { ModalService } from 'mbs-ui-kit';
import { DestinationPlusRetentionStepValue } from 'wizards/models/combined/destination-plus-retention-model';
import { RetentionPolicyStepValue } from 'wizards/models/retention-policy-models';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import {
  RetentionPolicyStepLogic,
  RetentionPolicyStepLogicHook
} from 'wizards/steps/retention-policy-step/NBF/retention-policy-step.logic';

// The schedule step shows retention alerts that RetentionPolicyStepLogic publishes; a plan wizard runs that logic in the retention
// step next to the schedule step, a template modal has no such step, so the logic runs here without UI. Create it in an injection
// context and before the storage context is published, as the logic's subscriptions do not replay
@UntilDestroy()
export class TemplateRetentionAlerts implements RetentionPolicyStepLogicHook {
  public readonly isHybrid: WritableSignal<boolean> = signal(false);
  public readonly isNBF = true;
  public retentionValue: RetentionPolicyStepValue;

  private readonly logic = new RetentionPolicyStepLogic(inject(WizardStepsService), inject(ModalService), inject(I18NextPipe), this);
  private readonly form: FormGroup;

  constructor() {
    this.logic.initIsHybrid(this.isHybrid);
    this.form = this.logic.getForm();
    this.logic.initEvents();
    this.logic.initFormEvents();

    inject(DestroyRef).onDestroy(() => this.ngOnDestroy());
  }

  public apply(destinationPlusRetention: DestinationPlusRetentionStepValue): void {
    this.isHybrid.set(!!destinationPlusRetention?.whereBackupStep?.IsHybridBackup);
    // updateForm writes the default period into the value it is handed
    this.logic.updateForm({ ...destinationPlusRetention?.retentionPolicyStep });
  }

  public removeError(controls: AbstractControl[], error: string): void {
    controls.forEach((control) => {
      const errors = { ...control.errors };

      delete errors[error];

      control.setErrors(Object.keys(errors).length ? errors : null);
    });
  }

  public toggleFormControls(controlNames: string[], isEnabled: boolean, stepForm?: FormGroup): void {
    controlNames.forEach((name) => (stepForm ?? this.form).get(name)?.[isEnabled ? 'enable' : 'disable']());
  }

  // UntilDestroy wraps this method to complete the logic's subscriptions
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public ngOnDestroy(): void {}
}
